import { ta } from '../i18n/adminT';

const API_BASE = process.env.REACT_APP_API_BASE || '/api';
export { API_BASE };

// Converte l'oggetto filtri della pagina Registrazioni in parametri di query,
// omettendo quelli vuoti (usato sia per l'elenco che per l'export CSV).
function toRegistrationsParams({ search = '', riferimento = '', ditta = '', dateFrom = '', dateTo = '' } = {}) {
  const params = {};
  if (search) params.search = search;
  if (riferimento) params.riferimento = riferimento;
  if (ditta) params.ditta = ditta;
  if (dateFrom) params.dateFrom = dateFrom;
  if (dateTo) params.dateTo = dateTo;
  return params;
}


// Messaggi d'errore del server (in italiano): nel pannello amministratore
// vengono mostrati nella lingua scelta dall'utente, se esiste la traduzione.
// Le rotte pubbliche (visitatori) restano invariate.
function adminMessage(path, message) {
  return path.startsWith('/admin') || path.startsWith('/auth') ? ta(message) : message;
}

async function request(path, { method = 'GET', body, isForm = false } = {}) {
  const res = await fetch(`${API_BASE}${path}`, {
    method,
    credentials: 'include',
    headers: isForm ? undefined : { 'Content-Type': 'application/json' },
    body: body ? (isForm ? body : JSON.stringify(body)) : undefined,
  });

  let data = null;
  try {
    data = await res.json();
  } catch {
    // risposta senza corpo JSON
  }

  if (!res.ok) {
    const message = data?.error || 'Si è verificato un errore imprevisto.';
    throw new Error(adminMessage(path, message));
  }
  return data;
}

export const api = {
  // pubblico
  register: (payload) => request('/public/registrations', { method: 'POST', body: payload }),
  logAccess: (numero_badge, action) => request('/public/access-log', { method: 'POST', body: { numero_badge, action } }),
  getLogo: () => request('/public/logo').catch(() => null),
  getDocPreviewConfig: () => request('/public/doc-preview-config'),
  saveDocPreviewConfig: (config) => request('/admin/doc-preview-config', { method: 'PUT', body: config }),
  getKioskConfig: () => request('/public/kiosk-config'),
  saveKioskConfig: (config) => request('/admin/kiosk-config', { method: 'PUT', body: config }),
  getSignatureConfig: () => request('/public/signature-config'),
  saveSignatureConfig: (config) => request('/admin/signature-config', { method: 'PUT', body: config }),
  getBadgeRegistrationConfig: () => request('/public/badge-registration-config'),
  // Impostazione generale (lettura admin): getBadgeRegistrationConfig, invece, dà il valore effettivo per il dispositivo corrente
  getBadgeRegistrationConfigAdmin: () => request('/admin/badge-registration-config'),
  saveBadgeRegistrationConfig: (config) => request('/admin/badge-registration-config', { method: 'PUT', body: config }),
  getDocumentFieldConfig: () => request('/public/document-field-config'),
  // Lingue attive nel pannello utente e lingua predefinita (lettura pubblica, salvataggio solo super-admin)
  getLanguageConfig: () => request('/public/language-config'),
  saveLanguageConfig: (config) => request('/admin/language-config', { method: 'PUT', body: config }),
  saveDocumentFieldConfig: (config) => request('/admin/document-field-config', { method: 'PUT', body: config }),
  getAutoCloseConfig: () => request('/admin/auto-close-config'),
  saveAutoCloseConfig: (config) => request('/admin/auto-close-config', { method: 'PUT', body: config }),
  getWelcomeEmailTemplate: () => request('/admin/welcome-email-template'),
  saveWelcomeEmailTemplate: (template) => request('/admin/welcome-email-template', { method: 'PUT', body: template }),
  getBadgeCheckinEmailConfig: () => request('/admin/badge-checkin-email-config'),
  saveBadgeCheckinEmailConfig: (enabled) => request('/admin/badge-checkin-email-config', { method: 'PUT', body: { enabled } }),
  getContactEmailTemplate: () => request('/admin/event-contact-email-template'),
  saveContactEmailTemplate: (template) => request('/admin/event-contact-email-template', { method: 'PUT', body: template }),
  externalCheckinLookup: (payload) => request('/public/external-checkin/lookup', { method: 'POST', body: payload }),
  externalCheckinConfirm: (payload) => request('/public/external-checkin/confirm', { method: 'POST', body: payload }),
  getBadgeSignatureConfig: () => request('/public/badge-signature-config'),
  saveBadgeSignatureConfig: (config) => request('/admin/badge-signature-config', { method: 'PUT', body: config }),
  searchReferenceContacts: (q) => request(`/public/reference-contacts/search?q=${encodeURIComponent(q)}`),

  // autenticazione
  login: (username, password) => request('/auth/login', { method: 'POST', body: { username, password } }),
  verify2fa: (tempToken, code) => request('/auth/verify-2fa', { method: 'POST', body: { tempToken, code } }),
  enable2faInit: () => request('/auth/2fa/enable/init', { method: 'POST' }),
  enable2faConfirm: (code) => request('/auth/2fa/enable/confirm', { method: 'POST', body: { code } }),
  disable2fa: (password) => request('/auth/2fa/disable', { method: 'POST', body: { password } }),
  logout: () => request('/auth/logout', { method: 'POST' }),
  me: () => request('/auth/me'),
  setMyLanguage: (language) => request('/auth/me/language', { method: 'PUT', body: { language } }),
  forgotPassword: (email) => request('/auth/forgot-password', { method: 'POST', body: { email } }),
  resetPassword: (token, newPassword) => request('/auth/reset-password', { method: 'POST', body: { token, newPassword } }),
  changePassword: (currentPassword, newPassword) => request('/admin/change-password', { method: 'POST', body: { currentPassword, newPassword } }),
  changeEmail: (newEmail, currentPassword) => request('/admin/change-email', { method: 'POST', body: { newEmail, currentPassword } }),

  // admin
  // Senza page restituisce l'elenco completo (usato dalla rubrica di selezione); con page è paginato
  getReferenceContacts: (search = '', page, pageSize) => request(`/admin/reference-contacts?search=${encodeURIComponent(search)}${page ? `&page=${page}&pageSize=${pageSize}` : ''}`),
  createReferenceContact: (payload) => request('/admin/reference-contacts', { method: 'POST', body: payload }),
  updateReferenceContact: (id, payload) => request(`/admin/reference-contacts/${id}`, { method: 'PUT', body: payload }),
  deleteReferenceContact: (id) => request(`/admin/reference-contacts/${id}`, { method: 'DELETE' }),

  // contatti (fiere, eventi e altri contesti)
  submitEventContact: (payload) => request('/public/event-contacts', { method: 'POST', body: payload }),
  getEventContactConfigPublic: () => request('/public/event-contact-config'),
  getEventContactConfig: () => request('/admin/event-contact-config'),
  saveEventContactConfig: (config) => request('/admin/event-contact-config', { method: 'PUT', body: config }),
  getEventContacts: (filters = {}, page = 1, pageSize = 50) => {
    const params = new URLSearchParams({ ...filters, page, pageSize });
    return request(`/admin/event-contacts?${params.toString()}`);
  },
  deleteEventContact: (id) => request(`/admin/event-contacts/${id}`, { method: 'DELETE' }),
  exportEventContactsCsv: async (filters = {}) => {
    const params = new URLSearchParams(filters);
    const res = await fetch(`${API_BASE}/admin/event-contacts/export?${params.toString()}`, {
      credentials: 'include',
    });
    if (!res.ok) {
      let message = 'Esportazione non riuscita.';
      try {
        const data = await res.json();
        if (data?.error) message = data.error;
      } catch {
        // risposta non in formato JSON
      }
      throw new Error(ta(message));
    }
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'contatti.csv';
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
  },
  getRegistrations: (filters = {}, page = 1, includeSignature = false, pageSize = 50) => {
    const params = new URLSearchParams({ ...toRegistrationsParams(filters), page, pageSize });
    if (includeSignature) params.set('includeSignature', '1');
    return request(`/admin/registrations?${params.toString()}`);
  },
  getRegistrationDetail: (id) => request(`/admin/registrations/${id}`),
  closeRegistration: (id) => request(`/admin/registrations/${id}/close`, { method: 'POST' }),
  deleteRegistration: (id) => request(`/admin/registrations/${id}`, { method: 'DELETE' }),
  exportRegistrationsCsv: async (filters = {}) => {
    const params = new URLSearchParams(toRegistrationsParams(filters));
    const res = await fetch(`${API_BASE}/admin/registrations/export?${params.toString()}`, {
      credentials: 'include',
    });
    if (!res.ok) {
      let message = 'Esportazione non riuscita.';
      try {
        const data = await res.json();
        if (data?.error) message = data.error;
      } catch {
        // risposta non in formato JSON
      }
      throw new Error(ta(message));
    }
    const blob = await res.blob();
    const disposition = res.headers.get('Content-Disposition') || '';
    const match = disposition.match(/filename="?([^"]+)"?/);
    const filename = match ? match[1] : 'ingressi.csv';

    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
  },
  getAccessLog: () => request('/admin/access-log'),
  getDocuments: () => request('/admin/documents'),
  uploadDocument: (type, lang, file) => {
    const form = new FormData();
    form.append('file', file);
    return request(`/admin/documents/${type}/${lang}`, { method: 'POST', body: form, isForm: true });
  },
  uploadLogo: (file) => {
    const form = new FormData();
    form.append('file', file);
    return request('/admin/logo', { method: 'POST', body: form, isForm: true });
  },
  saveLogoScale: (scale) => request('/admin/logo-scale', { method: 'PUT', body: { scale } }),
  getAdmins: () => request('/admin/admins'),
  createAdmin: (payload) => request('/admin/admins', { method: 'POST', body: payload }),
  deleteAdmin: (id) => request(`/admin/admins/${id}`, { method: 'DELETE' }),
  getAuditLog: () => request('/admin/audit-log'),
  getLoginLog: () => request('/admin/login-log'),

  // utenti esterni
  getExternalUsers: (search = '', page, pageSize) => request(`/admin/external-users?search=${encodeURIComponent(search)}${page ? `&page=${page}&pageSize=${pageSize}` : ''}`),
  createExternalUser: (payload) => request('/admin/external-users', { method: 'POST', body: payload }),
  updateExternalUser: (id, payload) => request(`/admin/external-users/${id}`, { method: 'PUT', body: payload }),
  regenerateExternalCode: (id) => request(`/admin/external-users/${id}/regenerate-code`, { method: 'POST' }),
  deleteExternalUser: (id) => request(`/admin/external-users/${id}`, { method: 'DELETE' }),

  // impostazioni SMTP (solo super-admin)
  getSmtpSettings: () => request('/admin/smtp-settings'),
  saveSmtpSettings: (payload) => request('/admin/smtp-settings', { method: 'PUT', body: payload }),
  testSmtpSettings: (to) => request('/admin/smtp-settings/test', { method: 'POST', body: { to } }),
  getSmtpOAuthMicrosoftStatus: () => request('/admin/smtp-oauth-microsoft/status'),
  disconnectSmtpOAuthMicrosoft: () => request('/admin/smtp-oauth-microsoft/disconnect', { method: 'POST' }),

  // supervisor (accesso limitato, senza 2FA propria)
  getSupervisorAdmins: () => request('/supervisor/admins'),
  resetAdmin2fa: (id) => request(`/supervisor/admins/${id}/reset-2fa`, { method: 'POST' }),

  // backup: esporta/importa impostazioni, registrazioni e utenti esterni (solo super-admin)
  exportBackup: async () => {
    const res = await fetch(`${API_BASE}/admin/backup/export`, { credentials: 'include' });
    if (!res.ok) {
      let message = 'Esportazione non riuscita.';
      try {
        const data = await res.json();
        if (data?.error) message = data.error;
      } catch {
        // risposta non in formato JSON
      }
      throw new Error(ta(message));
    }
    const blob = await res.blob();
    const disposition = res.headers.get('Content-Disposition') || '';
    const match = disposition.match(/filename="?([^"]+)"?/);
    const filename = match ? match[1] : 'exelflow-backup.json';

    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
  },
  importBackup: (payload) => request('/admin/backup/import', { method: 'POST', body: payload }),

  // backup della cartella licenza (license.lic + public_key.pem)
  exportLicenseBackup: async () => {
    const res = await fetch(`${API_BASE}/admin/license/export`, { credentials: 'include' });
    if (!res.ok) {
      let message = 'Esportazione non riuscita.';
      try {
        const data = await res.json();
        if (data?.error) message = data.error;
      } catch {
        // risposta non in formato JSON
      }
      throw new Error(ta(message));
    }
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'exelflow-license-backup.zip';
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
  },
  importLicenseBackup: (file) => {
    const form = new FormData();
    form.append('file', file);
    return request('/admin/license/import', { method: 'POST', body: form, isForm: true });
  },

  // stato e attivazione della licenza (pubblico lo status, upload solo super-admin)
  getLicenseStatus: () => request('/license/status'),
  getDeviceStatus: () => request('/public/device-status'),
  activateDevice: (code) => request('/public/device-activation', { method: 'POST', body: { code } }),
  getDeviceAuthConfig: () => request('/admin/device-auth-config'),
  saveDeviceAuthConfig: (config) => request('/admin/device-auth-config', { method: 'PUT', body: config }),
  getAuthorizedDevices: () => request('/admin/authorized-devices'),
  generateDeviceCode: () => request('/admin/authorized-devices/generate-code', { method: 'POST' }),
  renameAuthorizedDevice: (id, label) => request(`/admin/authorized-devices/${id}`, { method: 'PUT', body: { label } }),
  setDeviceNfcReader: (id, enabled) => request(`/admin/authorized-devices/${id}/nfc-reader`, { method: 'PUT', body: { enabled } }),
  revokeAuthorizedDevice: (id) => request(`/admin/authorized-devices/${id}`, { method: 'DELETE' }),
  uploadLicense: (file) => {
    const form = new FormData();
    form.append('file', file);
    return request('/license/upload', { method: 'POST', body: form, isForm: true });
  },
  uploadPublicKey: (file, advancedPassword) => {
    const form = new FormData();
    form.append('file', file);
    form.append('advancedPassword', advancedPassword);
    return request('/license/upload-public-key', { method: 'POST', body: form, isForm: true });
  },
};
